import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomToggle from '../../../components/toggle';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetBrokerSsoKeyManagementList,
  useGetTablePreferences,
  usePostBrokerSsoConfiguration,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import useGetBrokerOptions from '../../../hooks/get/useGetBrokerOptions';
import { type ModalStatusType } from '../../../types/common';
import { BROKER_SSO_CONFIGURATION_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';
import FormSelect from '../../../components/dropdown/formSelect';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrokerSsoConfigurationKeyService } from '@wealthlane/api-services/wealthlane-identity-service';
import { getFilteredPermissionData } from '../../../utils';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import { brokerSsoKeyManagementHeader } from '../../../constants/table-headers/brokerSsoKeyManagementHeader';
import Spinners from '../../../components/spinner';
import Button from '../../../components/button';
import AddKeyManagementModal from './addKeyManagementModal';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import AlertPopup from '../../../components/alert-popup';
import InitialValuesForm from '../../../constants/initialValues';
interface KeyManagementModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  id?: string;
  initialData?: any;
}

const KeyManagementModal = ({
  isModalOpen,
  closeModal,
  id,
  initialData,
}: KeyManagementModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneIdentityApi();
  const userAuthList = useSelector(selectAuthRoutesList);
  const { fieldLevelPermissionsGroups } = getFilteredPermissionData(
    userAuthList?.auth?.grantedPolicies ?? {}
  );
  const hasBrokerSsoKeyFieldPermission =
    (fieldLevelPermissionsGroups?.BrokerSsoKey?.length ?? 0) > 0;

  const [modalLoading, setModalLoading] = useState(false);
  const [headerList, setHeaderList] = useState<string[]>([]);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialDataKey, setInitialDataKey] = useState<any>(null);
  const {
    value: isAddKeyModalOpen,
    setFalse: closeAddKeyModal,
    setTrue: openAddKeyModal,
  } = useBoolean(false);
  const queryClient = useQueryClient();
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);

  //   const { mutate, isLoading } = usePostBrokerSsoConfiguration(
  //     handleRequest,
  //     closeModal,
  //     initialData.id
  //   );

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'KEY_MANAGEMENT_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { tableInfo } = useDynamicTable();

  const {
    data: brokerSsoKeyManagementList,
    status,
    isLoading: brokerSsoKeyManagementListLoading,
  } = useGetBrokerSsoKeyManagementList(handleRequest, initialData?.id);

  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Key Management');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Key Management',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteBrokerSsoConfigurationKeyById(customValue);
  };

  const deleteBrokerSsoConfigurationKeyById = (id: string): void => {
    setModalLoading(true);
    handleRequest(
      BrokerSsoConfigurationKeyService.deleteApiIdentityApiAppBrokerSsoConfigurationKey(id)
    )
      .then((data: any) => {
        if (data != null) {
          void queryClient.invalidateQueries('getBrokerSsoKeyManagementList');

          toast.success(
            <ToastComponent
              label={'Success'}
              message={'Brokerage SSO Configuration Key deleted.'}
            />
          );
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  const getBrokerSsoConfigurationKeyById = (id: string): any => {
    void handleRequest(
      BrokerSsoConfigurationKeyService.getApiIdentityApiAppBrokerSsoConfigurationKey(id)
    )
      .then((data: any) => {
        if (data != null) {
          setInitialDataKey({
            id,
            data: {
              ...data,
            },
          });
          openAddKeyModal();
        }
      })
      .finally(() => {});
  };

  const tableActions = [
    {
      id: 'view',
      action: 'View Keys',
      as: 'Edit',
      isHidden: () => !hasBrokerSsoKeyFieldPermission,
      onClick: async (id: string, rowData: Record<string, string>) => {
        setModalStatus('View');
        getBrokerSsoConfigurationKeyById(id);
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      as: 'Edit',
      onClick: async (id: string) => {
        setModalStatus('Edit');
        getBrokerSsoConfigurationKeyById(id);
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      onClick: (id: string) => {
        // setModalStatus('Delete');
        setCustomValue(id);
        openConfirmModal();
      },
    },
  ];

  const tableHeaderList = brokerSsoKeyManagementHeader({
    actions: schemaData?.columns,
    tableActions,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const brokerSsoConfigurationListHeader = (): any[] => {
    return brokerSsoKeyManagementHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: brokerSsoConfigurationListHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData, headerList, hasBrokerSsoKeyFieldPermission]
  );

  const data = React.useMemo(
    () => brokerSsoKeyManagementList?.items ?? [],
    [brokerSsoKeyManagementList, brokerSsoKeyManagementListLoading]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        title='Add Key'
        variant='primary'
        icon={'add_circle_outline'}
        id='brokerSsoKeyManagementAddKeyButton'
        onClick={() => {
          setModalStatus('Add');
          setInitialDataKey(null);
          openAddKeyModal();
        }}
      />
    );
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{}}
        //   validationSchema={BROKER_SSO_CONFIGURATION_VALIDATION}
        onSubmit={(values) => {
          // mutate(values);
        }}>
        {({ touched, handleChange, errors, values, setFieldValue }) => {
          return (
            <ModalComponent
              show={isModalOpen}
              closeModal={closeModal}
              loading={false}
              size='xxl'
              // valueEdited={false}
              title={`Key Management`}
              id={id}
              children={
                <Form id='brokerageSsoConfigurationForm'>
                  <ModalBody id='modalBody'>
                    <DynamicTable
                      id='commissionDataTable'
                      schema={schema}
                      data={data}
                      loading={status}
                      // filterControls={controls}
                      totalCounts={brokerSsoKeyManagementList?.totalCount ?? 10}
                      components={{ renderRightToolbarButton }}
                      tableColumnList={tableHeaderList}
                      selectedColumnList={headerList}
                      tablePreferenceLoading={tablePrefLoading}
                      handleColumnSave={handleSavePreference}
                    />
                  </ModalBody>
                  <ModalFooterComponent
                    id='brokerageSsoConfigurationModalFooter'
                    modalStatus={'History'}
                    onCloseModal={() => {
                      closeModal();
                    }}
                    onEditModal={() => {
                      // setModalStatus('Edit');
                    }}
                    //   valueChanged={_.isEqual(values, initialData.data)}
                    loading={false}
                  />
                </Form>
              }
            />
          );
        }}
      </Formik>

      <AnimationWrapper id='addKeyManagementModalAnimationWrapper'>
        {isAddKeyModalOpen && (
          <AddKeyManagementModal
            isModalOpen={isAddKeyModalOpen}
            closeModal={closeAddKeyModal}
            id={id}
            initialData={initialDataKey}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            brokerSsoConfigurationId={initialData?.id}
          />
        )}
      </AnimationWrapper>
      {isConfirmOpen && (
        <AlertPopup
          id='brokerageSsoConfigurationKeyManagementDeleteAlert'
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this Broker SSO Configuration Key'}
          handleSubmit={onPrimaryClick}
          // setRemark={setRemark}
          // remark={remark}
          loading={modalLoading}
        />
      )}
    </>
  );
};

export default KeyManagementModal;
